import * as React from 'react'
import { Activity, Loader2, RefreshCw, Square } from 'lucide-react'
import { RUN_ACTIVE_PHASES, displayRunPhase } from '@acs/shared'
import {
  useHuntLive,
  useRunLog,
  useRunState,
  useStartAutomation,
  useStopAutomation,
  type RunState,
} from '@/hooks/use-automation'
import { useCountdown, useLiveElapsed } from '@/hooks/use-relative-time'
import { apiErrorCode } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Drawer } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { RunConsole, RunPhasePill } from '@/components/session/run-console'
import { HuntPanel } from '@/components/session/hunt-panel'
import { LoginLinkMenu } from '@/components/session/login-link-menu'
import {
  GenderGateDialog,
  type GenderGateAsk,
  type GenderGateRow,
} from '@/components/customers/gender-gate-dialog'

/**
 * The live view of a run this system is performing.
 *
 * Separate from `SessionDrawer`, which is keyed by the legacy `sessionId` and
 * reads the other project's `session_logs`. A run started here has neither, so
 * this is keyed by customer and reads our own run channel.
 *
 * The drawer is a shell: it owns the start, the stop and the framing, and hands
 * the data to `RunConsole`. Everything on screen arrives over SSE — nothing here
 * polls.
 */

/** Phases where a run is genuinely in progress, so Stop is the useful control. */
const ACTIVE: ReadonlySet<string> = new Set<string>(RUN_ACTIVE_PHASES)

export function RunDrawer({
  customerId,
  onClose,
  autoStart = false,
}: {
  customerId: string | null
  onClose: () => void
  /** Begin a run as soon as this opens — what the row's Start button wants. */
  autoStart?: boolean
}) {
  const { data, isLoading } = useRunLog(customerId)
  const liveState = useRunState(customerId)
  const start = useStartAutomation()
  const stop = useStopAutomation()

  /*
   * The live map first, then the seed the log request carried.
   *
   * `useRunState` reads the run map, which holds what the API process has in
   * MEMORY — and it drops a finished run ten minutes later. The log itself is
   * kept for two weeks, so past that ten minutes this drawer rendered a full
   * failed run with no phase, no counters and no elapsed time against it: the
   * evidence without the verdict.
   *
   * The `/run-log` response already carries `state`, and now answers it from the
   * stored snapshot when memory has nothing. Same fallback shape as `hunt`
   * below, and same ordering rule — live outranks seed, because for a running
   * customer the map is the fresher of the two.
   */
  const state = liveState ?? data?.state ?? null

  /*
   * `live` OUTRANKS the phase — the same rule the customer row has always used.
   *
   * The DTO has carried `live` all along (the API sets it from the machinery
   * registries), and this component was the one place that ignored it and
   * decided from `phase` alone. That is what made the drawer contradict itself
   * on 2026-08-07: a stale `failed` stamp from an abandoned sign-in left a
   * perfectly healthy parked customer reading FAILED, which swapped Stop for
   * "Run again", which POSTed /start, which was refused 409 by the very watch
   * that proved the customer was fine.
   *
   * A customer parked on the schedule watch has no heartbeat and no hunt, so
   * `live` is the ONLY field that reports them as working. Reading it here is
   * what makes Stop stay available for exactly as long as there is something to
   * stop.
   */
  const phase = displayRunPhase(state)
  const isRunning = state?.live === true || ACTIVE.has(phase)

  /*
   * The hunt is polled while the run is live, not while a hunt is known.
   *
   * Gating on "we already have a hunt" was the bug: the drawer opens at the
   * start of a run and the hunt does not exist until the application is
   * created, so a gate that needed one could never open. `isRunning` covers
   * `applying` and `holding`, which is every phase a hunt can exist in, and it
   * is true from the moment the operator presses Start — so the panel appears
   * within a second of the workers launching rather than not at all.
   */
  /*
   * The selected worker lives HERE, not inside the panel, because it is a
   * request parameter.
   *
   * A worker's call log and its kept failures are the only expensive part of the
   * hunt response, so they are fetched only for the worker somebody is reading —
   * which means the component that owns the query has to know which one that is.
   * The panel still drives it; it just no longer owns it.
   *
   * Reset on every customer change, so a filter set while looking at one
   * customer cannot silently follow the operator to the next.
   */
  const [worker, setWorker] = React.useState<number | null>(null)
  React.useEffect(() => setWorker(null), [customerId])

  const { data: huntLive } = useHuntLive(customerId, isRunning, worker)

  /*
   * The drawer OWNS the start, rather than the caller firing it and hoping.
   *
   * When the table did `startRun.mutate(id)` and separately opened this, a
   * rejected start — a customer with no PIN on file (404), one already running
   * (409) — was swallowed by a mutation nobody was watching. The drawer opened,
   * sat at "No run recorded", and said nothing. Owning the call means the
   * pending and error states are rendered right where the operator is looking.
   *
   * `attempted` guards against React's double-invoked effects in StrictMode and
   * against a re-render firing a second start for a run already in flight.
   */
  const attempted = React.useRef<string | null>(null)
  const startRef = React.useRef(start)
  startRef.current = start

  /*
   * ONE effect, because the reset and the start are ordered with respect to each
   * other and two effects on the same dependency are not.
   *
   * React runs effects in declaration order, so a separate "clear on change"
   * effect declared after this one would fire immediately AFTER the start —
   * wiping `attempted` and resetting the mutation that had just been dispatched,
   * which re-arms the guard and lets the next render fire a SECOND start. For a
   * flow whose first act is emailing a verification code, that is the one bug
   * worth writing an awkward effect to avoid.
   *
   * `attempted` guards against React's double-invoked effects in StrictMode and
   * against a re-render firing a second start for a run already in flight. The
   * reset also clears a failed Start's error banner, which otherwise followed
   * the operator to the next customer it was opened on — the mutation outlives
   * every open and close, since this drawer is permanently mounted.
   */
  React.useEffect(() => {
    if (attempted.current !== customerId) {
      attempted.current = null
      startRef.current.reset()
    }
    if (!autoStart || customerId === null) return
    if (attempted.current === customerId) return
    attempted.current = customerId
    startRef.current.mutate({ customerId })
  }, [autoStart, customerId])

  /*
   * The gender gate, from THIS side of the table.
   *
   * The table asks before it sends, because it has every row's `gender` in
   * hand. This drawer has only a customer id, so it asks the other way round:
   * it sends, and when the server answers `gender_required` it opens the same
   * dialog for this one customer and re-sends with the answer. Same dialog,
   * same server write, same one-request start — just triggered by the refusal
   * rather than ahead of it.
   *
   * `dismissed` remembers which refusal the operator closed without answering,
   * so Cancel does not reopen the question on the next render while the
   * refusal itself stays readable in the console banner below. A NEW refusal —
   * the next click — is a different error object and asks again.
   */
  const [dismissed, setDismissed] = React.useState<unknown>(null)
  const startError = start.error
  const genderAsk = React.useMemo<GenderGateAsk<GenderGateRow> | null>(() => {
    if (customerId === null || startError === null || startError === dismissed) return null
    if (apiErrorCode(startError) !== 'gender_required') return null
    const row: GenderGateRow = {
      id: customerId,
      customerId,
      name: state?.name ?? '',
      email: state?.email ?? '',
    }
    return { targets: [row], missing: [row] }
  }, [customerId, startError, dismissed, state?.name, state?.email])

  const lines = data?.lines ?? EMPTY_LINES
  /*
   * The heartbeat, from the live poll whenever the poll carries it.
   *
   * The run-log seed is fetched once, when the drawer opens, so on its own it
   * gave a "Next beat in 45s" that never counted down, never moved on after a
   * beat — and was absent altogether from a drawer opened by Start, since the
   * heartbeat begins a minute or so later. The poll answers every 700ms while
   * the run is live; the seed now only covers the moment before its first
   * answer, or an API that predates the field.
   *
   * Hidden once the run stops: the poll stops with it, and its last answer
   * would otherwise count down to a beat that is never coming.
   */
  const polled = huntLive !== undefined && huntLive.keepAlive !== undefined ? huntLive : null
  const keepAlive = !isRunning
    ? null
    : polled !== null
      ? (polled.keepAlive ?? null)
      : (data?.keepAlive ?? null)
  const beatDueAt =
    polled !== null && keepAlive !== null ? polled.receivedAt + keepAlive.nextInMs : null
  /*
   * The live poll wins; the run-log seed only covers the first ~700ms.
   *
   * `huntLive` is `undefined` before its first response and `{hunt: null}` once
   * it has answered — so the seed fills the opening gap when a console is
   * re-opened onto an already-running hunt, and is then completely superseded
   * rather than being able to resurrect a hunt that has since finished.
   */
  const hunt = huntLive === undefined ? (data?.hunt ?? null) : huntLive.hunt

  return (
    <Drawer
      open={customerId !== null}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      title={
        <span className="flex items-center gap-2">
          {isRunning ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin text-accent" />
          ) : (
            <Activity aria-hidden="true" className="size-4 text-faint" />
          )}
          <span>Live run</span>
          <RunPhasePill phase={phase} />
        </span>
      }
      /*
       * The description keeps only what is about the SESSION, not the person.
       *
       * The browser identity and the endpoint answer "why did this one call
       * fail when the others did not", which is a question about the run. Who
       * the customer is has moved to the trailing edge of the header — see
       * `headerExtra` — because it is the thing an operator checks they are
       * looking at the right drawer, and that belongs where the eye lands last
       * rather than buried among five middle dots.
       */
      description={
        customerId === null ? undefined : (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {state?.agent ? <span className="text-faint">{state.agent}</span> : null}
            {state?.egress ? (
              <>
                {state?.agent ? (
                  <span aria-hidden="true" className="text-faint">
                    ·
                  </span>
                ) : null}
                <span className="tabular text-faint">{state.egress}</span>
              </>
            ) : null}
          </span>
        )
      }
      headerExtra={
        customerId === null ? null : (
          <RunIdentityStrip customerId={customerId} state={state} running={isRunning} />
        )
      }
      /*
       * Full width, because the content is now two columns.
       *
       * At 64rem the hunt sat above the log in a 26rem letterbox — five worker
       * rows and a scrollbar. The workers and the customer's own calls are read
       * TOGETHER (a refusal in one explains a stall in the other), so they belong
       * side by side, and side by side needs the screen.
       */
      widthClass="md:w-screen md:max-w-none"
      footer={
        /*
         * `min-w-0` on the status text and `flex-none` on the actions, because a
         * third button now shares this row.
         *
         * A flex child will not shrink below its content by default, so the
         * status line — "Next beat in 45s · 30.2s after its 200" — was free to
         * push the buttons off the end of a narrow viewport rather than
         * ellipsing. With two buttons that never quite happened; with Login link
         * beside them it does. The controls are what the footer is FOR, so they
         * keep their width and the commentary gives way.
         */
        <div className="flex items-center justify-between gap-3">
          {/*
            A store that cannot be read outranks the beat countdown.

            Without this an unreachable store renders as a log with no lines,
            which is indistinguishable from a run that has not written any — and
            the run itself keeps going either way, so the operator would be
            reading an empty screen about a customer who is working fine.
          */}
          {data?.degraded === true ? (
            <span className="min-w-0 flex-1 text-[12px] text-warn">
              log store unreachable — the run is unaffected, its history is not being shown
            </span>
          ) : (
            /*
              Hidden on a phone, where three buttons leave it about 60px — a
              beat countdown ellipsed to "Next be…" is not information, and the
              controls beside it are. The degraded warning above keeps its place
              at every width, because that one is not commentary.
            */
            <span className="min-w-0 flex-1 truncate text-[12px] text-faint max-sm:hidden">
              {keepAlive?.dormant ? (
                /*
                  Switched off, and said so rather than counted down.

                  A dormant heartbeat still has a due time — it wakes to re-read
                  the setting and to renew the session — so `nextInMs` is a real
                  number and rendering it would promise a beat that is never
                  coming. The seat is not being held; the run is otherwise fine,
                  which is what the second clause is there to say.
                */
                <span className="text-warn" title="The keep-alive interval is set to 0 in Settings → Limits. The seat is not being re-confirmed. The hunt, this customer's address and the session renewal are all unaffected.">
                  Keep-alive off — the seat is not being re-confirmed
                </span>
              ) : keepAlive ? (
                <>
                  <BeatCountdown dueAt={beatDueAt} fallbackMs={keepAlive.nextInMs} />
                  {/*
                    The measured distance, beside the predicted one.

                    The interval is counted from the last response a beat
                    RECEIVED, not from when it was dispatched — so a beat that
                    met a 405, solved it and retried is anchored on the 200 that
                    ended it. That is a claim about behaviour nobody could check
                    from this screen: the countdown looks identical either way,
                    and a detour would silently be paid for by the customer's
                    window if it were wrong.

                    So the achieved response-to-dispatch gap is shown next to it.
                    It should read the interval on every beat, detour or not. The
                    dispatch-to-dispatch gap runs longer by exactly the detour and
                    is in the tooltip, because the two being different is the
                    whole point rather than a discrepancy.
                  */}
                  {typeof keepAlive.lastSinceResponseMs === 'number' &&
                  keepAlive.lastSinceResponseMs > 0 ? (
                    <span
                      className="tabular"
                      title={
                        typeof keepAlive.lastGapMs === 'number'
                          ? `${(keepAlive.lastSinceResponseMs / 1000).toFixed(1)}s from the last response to this beat — the interval the scheduler holds. ${(
                              keepAlive.lastGapMs / 1000
                            ).toFixed(1)}s beat to beat, which runs longer by whatever a WAF detour cost.`
                          : undefined
                      }
                    >
                      {` · ${(keepAlive.lastSinceResponseMs / 1000).toFixed(1)}s after its 200`}
                    </span>
                  ) : null}
                </>
              ) : lines.length > 0 ? (
                `${lines.length} events`
              ) : (
                ''
              )}
            </span>
          )}
          <div className="flex flex-none gap-2">
            {/*
              Hand the session over to a person.

              Not gated on `isRunning`, deliberately: a session outlives the run
              that made it by up to an hour and fifty minutes, and the moment
              somebody most wants to finish an application by hand is right after
              the automation has stopped. Gating it on the run would take the
              control away exactly then.

              `key` is load-bearing rather than a list-diffing habit — the same
              trick `HuntPanel` uses below. This component holds a live
              credential in its mutation result, and re-keying it per customer
              means that result is thrown away when the operator moves on, and
              again when the drawer closes and `customerId` goes null.
            */}
            {customerId ? <LoginLinkMenu key={customerId} customerId={customerId} /> : null}
            {isRunning && customerId ? (
              <Button
                variant="danger"
                size="sm"
                loading={stop.isPending}
                disabled={stop.isPending}
                onClick={() => stop.mutate(customerId)}
              >
                <Square aria-hidden="true" className="size-3.5" />
                Stop
              </Button>
            ) : customerId ? (
              // Re-running a finished customer is the common next action after
              // reading why it failed, so it belongs here rather than making the
              // operator close this and find the row again.
              <Button
                variant="secondary"
                size="sm"
                loading={start.isPending}
                disabled={start.isPending}
                onClick={() => start.mutate({ customerId })}
              >
                <RefreshCw aria-hidden="true" className="size-3.5" />
                {phase === 'idle' ? 'Start' : 'Run again'}
              </Button>
            ) : null}
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      }
    >
      {/*
        Workers left, the customer's own calls right.

        These two are read together — a worker being refused explains a run that
        has gone quiet, and neither half means much alone — so stacking them put
        whichever one you were not looking at off screen. Below `lg` it falls
        back to a single column, because a narrow viewport cannot honestly show
        two.

        `h-full` on the grid and `min-h-0` on both cells is what lets each
        column scroll independently: without `min-h-0` a flex/grid child refuses
        to shrink below its content and both scrollbars end up on the page
        instead of inside the panels.
      */}
      <div className="grid h-full min-h-0 gap-2.5 lg:grid-cols-2">
        <div className="flex min-h-0 flex-col">
          {/*
            Keyed by customer so nothing inside the panel — an expanded body, a
            scroll position — can follow the operator from one run to the next.
          */}
          {hunt ? (
            <HuntPanel
              key={hunt.customerId}
              hunt={hunt}
              worker={worker}
              onSelectWorker={setWorker}
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-md border border-line bg-s1 px-4 text-center text-[12px] text-faint">
              {isRunning
                ? 'The workers start once the application exists — about a minute in.'
                : 'No hunt for this customer.'}
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-col">
          <RunConsole
            lines={lines}
            state={state}
            loading={isLoading}
            error={start.error ? errorMessage(start.error) : null}
            /*
             * The grid owns the height now.
             *
             * This used to be `h-[calc(100dvh-42rem)]`, derived from the panel's
             * fixed 26rem above it — two magic numbers that had to be kept in
             * step by hand and went negative on a short viewport. Columns do not
             * need either.
             */
            className="h-full min-h-0"
          />
        </div>
      </div>

      <GenderGateDialog
        ask={genderAsk}
        onCancel={() => setDismissed(startError)}
        onConfirm={(answers) => {
          if (customerId === null) return
          const gender = answers.get(customerId)
          if (gender === undefined) return
          start.mutate({ customerId, gender })
        }}
      />
    </Drawer>
  )
}

/**
 * Who this drawer is about, on the trailing edge of the header.
 *
 * Name, customer id, login and two clocks — the things an operator checks to be
 * sure they are looking at the right run, put where the eye lands last rather
 * than buried in a row of middle dots under the title.
 *
 * ── Why the session clock is HERE and not a tile ────────────────────
 * It was a counter tile, beside 403s and round trips, which framed it as a
 * measurement of the run. It is not — it is the age of the CREDENTIAL, the one
 * number on screen that predicts something rather than reporting it: the token
 * is re-minted at 90 minutes and stops working at about 1h50m. Read next to the
 * identity it belongs to, it says how much life this session has left. In a
 * grid of call counters it read as trivia.
 *
 * ── And why the run has a clock of its own ──────────────────────────
 * RUN answers the question the session clock only seemed to: how long has
 * this customer been going, from Start (or Run again) to the stop. The two
 * agree only on a first run. A restart reuses the stored session instead of
 * emailing a second code, so a run two minutes old can be carrying a token an
 * hour and a half old — and that pairing is exactly the one worth seeing at a
 * glance, which is why they share a box rather than being told apart by a
 * tooltip.
 *
 * Its own component so the once-a-second re-render lands here, and not on a
 * header that would otherwise repaint the phase pill and both columns' worth of
 * props with it.
 */
const RunIdentityStrip = React.memo(function RunIdentityStrip({
  customerId,
  state,
  running,
}: {
  customerId: string
  state: RunState | null
  running: boolean
}) {
  /*
   * From the LOGIN, falling back to the run start until one is known.
   *
   * These differ by more than a preamble: a restart reuses the stored session
   * rather than emailing a second code, so a fresh run can be carrying a token
   * that is already ninety minutes old. Timing from the run start would report
   * that as a minute — the opposite of the truth about the only clock that
   * decides anything.
   */
  const from = state?.metrics.signedInAtMs ?? state?.startedAt ?? null
  const fromLogin = state?.metrics.signedInAtMs != null
  // Frozen at the run's last activity once it stops: a session that ended
  // twenty minutes ago is not twenty minutes older than it was.
  const elapsed = useLiveElapsed(from, running ? null : (state?.updatedAt ?? null))

  /*
   * The run itself, Start to finish.
   *
   * Frozen at the API's own finish line rather than at the last log line: the
   * server stops `elapsedMs` at the moment the run failed or was stopped, so
   * `startedAt + elapsedMs` IS that moment, and a straggler line from a watch
   * noticing the stop a second later cannot stretch it. `startedAt` is reset
   * by Start and Run again and by nothing else, so this never restarts
   * mid-run — holding a seat for hours counts every one of them.
   */
  const runEnd =
    !running && state?.startedAt != null ? state.startedAt + state.metrics.elapsedMs : null
  const runElapsed = useLiveElapsed(state?.startedAt ?? null, runEnd)

  return (
    <div className="flex min-w-0 items-center gap-3 text-[12px]">
      <div className="flex min-w-0 flex-col items-end leading-tight">
        {state?.name ? (
          <span className="max-w-[16rem] truncate text-fg">{state.name}</span>
        ) : (
          <span className="tabular max-w-[16rem] truncate text-fg">{customerId}</span>
        )}
        <span className="flex min-w-0 items-center gap-1.5 text-[11px]">
          {state?.name ? (
            <span className="tabular truncate text-faint">{customerId}</span>
          ) : null}
          {state?.email ? (
            <span className="max-w-[18rem] truncate text-muted">{state.email}</span>
          ) : null}
        </span>
      </div>

      {/* One box, two cells: the same edge says "live" for both. */}
      <div
        className={cn(
          'flex flex-none items-stretch divide-x divide-line rounded-md border border-line border-l-2',
          running ? 'border-l-accent' : 'border-l-line'
        )}
      >
        <HeaderClock
          label="Run"
          value={runElapsed}
          live={running}
          title={
            running
              ? 'Since this run was started — Start or Run again. Stops when the run is stopped or fails.'
              : 'How long this run lasted, from Start to the moment it stopped or failed.'
          }
        />
        <HeaderClock
          label="Session"
          value={elapsed}
          live={running}
          title={
            fromLogin
              ? 'Since the sign-in these credentials came from — the original one, carried across a reused session. The token is re-minted at 90 minutes and stops working at about 1h50m.'
              : 'Since this run started. Switches to the sign-in time once one is known.'
          }
        />
      </div>
    </div>
  )
})

/** One cell of the header's clock box: a small caps label over a ticking value. */
function HeaderClock({
  label,
  value,
  live,
  title,
}: {
  label: string
  value: string
  live: boolean
  title: string
}) {
  return (
    <div title={title} className="flex flex-col items-end px-2.5 py-1 leading-tight">
      <span className="text-[9.5px] font-medium tracking-[0.07em] text-faint uppercase">
        {label}
      </span>
      <span className={cn('tabular font-mono text-[13px]', live ? 'text-accent' : 'text-fg')}>
        {value}
      </span>
    </div>
  )
}

/**
 * "Next beat in 45s", counting down between polls.
 *
 * Its own component so the once-a-second tick re-renders one span rather than
 * the drawer. `dueAt` is null only for a heartbeat read from the run-log seed,
 * which has no moment to count from, so that one shows what it was told.
 *
 * At zero the beat is due and says so. While a beat is out the server reports
 * the full interval as a lower bound, so the next poll puts a real number back.
 */
function BeatCountdown({ dueAt, fallbackMs }: { dueAt: number | null; fallbackMs: number }) {
  const left = useCountdown(dueAt)
  const s = left ?? Math.round(fallbackMs / 1000)
  return <span className="tabular">{s > 0 ? `Next beat in ${s}s` : 'Beat going out now'}</span>
}

/** Stable identity, so an empty log does not hand the console a new array. */
const EMPTY_LINES: never[] = []

/**
 * The server's own message, not a generic one.
 *
 * `ApiError` carries the API's `error.message` — "No customer … with a PIN and
 * IMAP password on file", "already running" — which is the entire diagnostic.
 * Replacing it with "Request failed" would throw away the only useful part.
 */
function errorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message
    if (typeof m === 'string' && m.length > 0) return m
  }
  return 'The server rejected the request.'
}
